/**
 * How long a notice is shown at all — the third axis next to sectioning
 * (tiers.ts) and priority (priorityTier.ts). Every notice is one of three kinds:
 *
 * - moment: about a point in time ("starts in 30 minutes", "3 conflicts
 *   starting soon"). It disappears once that moment is over, done or not.
 * - obligation: asks the user to act ("reply to Dana", "reconnect Gmail",
 *   "deadline passed"). It stays until it is done, dismissed, or expires.
 * - update: tells the user something happened. It stays for the retention
 *   window and falls into the background once seen.
 *
 * Any notice also ends at its producer-set `relevantUntil`, when it has one:
 * a heads-up about a date ("the filing is due Friday, October 16. Want a
 * reminder?") or a confirmed meeting means nothing once that date is over.
 */

import {
  ANCHOR_KEYS,
  FOLLOW_UP_TYPES,
  MOMENT_PASSED_MS,
  getNotificationAnchor,
  isTimeAnchoredNotification,
  needsUserAction,
  notificationKinds,
  notificationTime,
  type TierableNotification,
} from "@/src/features/notifications/tiers";
import { legacyDeviceHealthRelevantUntil } from "@/src/features/proactive-runtime/deviceHealth";
import { ownerCancelledCompletionRelevantUntil } from "@/src/features/jobs/ownerCancellation";
import { isOpenMustDo } from "@/src/features/notifications/overdueObligation";

export type NotificationLifecycle = "moment" | "obligation" | "update";

export type LifecycleNotification = TierableNotification & {
  topPriorityItem?: unknown;
  storedDate?: unknown;
  expiresAt?: unknown;
  /** Producer-set end of relevance (ms epoch), e.g. the last event a conflict bundle covers. */
  relevantUntil?: unknown;
  /** Device-health facts, lifted from extraFields; older rows end by them (legacyDeviceHealthRelevantUntil). */
  deviceHealth?: unknown;
  /** The job a completion belongs to; an owner-cancel marker id ends the row (ownerCancelledCompletionRelevantUntil). */
  sourceObjectId?: unknown;
};

/**
 * A todo nudge with no event behind it stays this long after the todo is due
 * (or, with no due date, after the todo was created). Past that the todo is
 * stale work, which the stale task digest owns; the nudge does not live on.
 * A must-do's nudge ends an hour after it is due instead: the overdue
 * obligation owns it from then (overdueObligation.ts).
 */
export const TODO_NUDGE_MS = 24 * 60 * 60 * 1000;

// Kinds about a moment, even when they also ask for action: a conflict that has started is moot.
const MOMENT_TYPES = new Set(["conflict_alert", "weather_alert"]);

// Kinds that ask for action and stay relevant after their anchor has passed.
const OBLIGATION_TYPES = new Set([
  "deadline_alert",
  "reply_attention",
  "obligation_follow_up",
  "integration_alert",
  "reauthorize_required",
  "integration_review",
]);

const record = (value: unknown): Record<string, unknown> | null =>
  value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;

// Everything the lifecycle reads to time a notice. Feed rows carry these on top, not in extraFields.
const TIMING_KEYS = [
  ...ANCHOR_KEYS,
  "events",
  "topPriorityEvent",
  "topPriorityItem",
  "bundleType",
  "relevantUntil",
  "expiresAt",
  "storedDate",
] as const;

export type NotificationTiming = Partial<Record<(typeof TIMING_KEYS)[number], unknown>>;

/** The timing fields of a feed row, so a client-built row can still tell when it is over. */
export function pickNotificationTiming(row: unknown): NotificationTiming {
  const source = record(row);
  if (!source) return {};
  const timing: NotificationTiming = {};
  for (const key of TIMING_KEYS) {
    if (source[key] !== undefined) timing[key] = source[key];
  }
  return timing;
}

export function getNotificationLifecycle(item: LifecycleNotification): NotificationLifecycle {
  const itemKinds = Array.from(notificationKinds(item));
  if (itemKinds.some((kind) => FOLLOW_UP_TYPES.has(kind))) return "obligation";
  if (isTimeAnchoredNotification(item) || itemKinds.some((kind) => MOMENT_TYPES.has(kind))) return "moment";
  if (needsUserAction(item) || itemKinds.some((kind) => OBLIGATION_TYPES.has(kind))) return "obligation";
  return "update";
}

/**
 * The producer-set end of relevance (ms epoch), on the row or in its
 * extraFields. Device-health rows from before they expired on their own,
 * and job completions of a cancellation the owner asked for, get the end
 * their producer now gives them.
 */
export function getProducerRelevantUntil(item: LifecycleNotification): number | null {
  return notificationTime(item.relevantUntil)
    ?? notificationTime(record(item.extraFields)?.relevantUntil)
    ?? legacyDeviceHealthRelevantUntil(item)
    ?? ownerCancelledCompletionRelevantUntil(item);
}

/**
 * When a moment notice stops being relevant (ms epoch), or null when it has no
 * time to go by. Measured from the start, not the end: an all-day holiday is
 * news at midnight, not all day.
 */
export function getMomentRelevantUntil(item: LifecycleNotification): number | null {
  const explicit = getProducerRelevantUntil(item);
  if (explicit) return explicit;
  const anchor = getNotificationAnchor(item);
  if (anchor) return anchor + MOMENT_PASSED_MS;
  const todo = record(item.topPriorityItem) ?? record(record(item.extraFields)?.topPriorityItem);
  if (!todo) return null;
  // Measured from the todo, never from the row: every feed run rewrites the
  // row's storedDate, so a nudge timed from it would never end while the todo
  // stays open. The row's own time is the last resort for a todo with neither.
  const due = notificationTime(todo.dueDate);
  // A must-do hands over to the overdue obligation once it is due, so its
  // nudge ends like any moment instead of repeating the overdue notice.
  if (due && isOpenMustDo(todo)) return due + MOMENT_PASSED_MS;
  const since = due
    ?? notificationTime(todo.createdDate)
    ?? notificationTime(item.storedDate);
  return since ? since + TODO_NUDGE_MS : null;
}

/** When a notice stops being shown (ms epoch), or null when only the user can end it. */
export function getNotificationRelevantUntil(item: LifecycleNotification): number | null {
  const expiresAt = notificationTime(item.expiresAt);
  // A moment also ends by its anchor; any other notice only by what its producer declared.
  const end = getNotificationLifecycle(item) === "moment" ? getMomentRelevantUntil(item) : getProducerRelevantUntil(item);
  if (expiresAt && end) return Math.min(expiresAt, end);
  return expiresAt ?? end;
}

export function isNotificationOver(item: LifecycleNotification, now: number): boolean {
  const until = getNotificationRelevantUntil(item);
  return until !== null && until <= now;
}
